import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StarRating } from "@/components/StarRating";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Circle, Clock } from "lucide-react";
import ColdChainLog from "@/components/ColdChainLog";
import { downloadInvoice } from "@/pages/BuyerPortal";
import { useCurrency } from "@/context/CurrencyContext";

const STEPS = [
  { key: "pending_payment", label: "Awaiting payment" },
  { key: "escrow_locked", label: "Funds locked in escrow" },
  { key: "health_verified", label: "Health certs verified" },
  { key: "dispatched", label: "Bio-courier dispatched (LN₂)" },
  { key: "released", label: "Escrow released to seller" },
];

export default function OrderDetail() {
  const { orderId } = useParams();
  const { fmt, fmtDual } = useCurrency();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [ratingStars, setRatingStars] = useState(5);
  const [ratingComment, setRatingComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const [disputeDetails, setDisputeDetails] = useState("");

  const reload = () => api.get(`/orders/${orderId}`).then(r => setOrder(r.data)).catch(() => setOrder(false));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [orderId]);

  if (!order) return <div className="p-10 text-center text-stone-500">Loading…</div>;

  const act = async (path) => {
    setBusy(true);
    try { await api.post(`/orders/${orderId}/${path}`); toast.success("Updated"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const submitRating = async () => {
    const target = user.user_id === order.buyer_id ? order.seller_id : order.buyer_id;
    try {
      await api.post("/ratings", { target_user_id: target, order_id: orderId, stars: ratingStars, comment: ratingComment });
      toast.success("Rating submitted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const raiseDispute = async () => {
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/dispute`, { reason: disputeReason, details: disputeDetails });
      toast.success("Dispute raised — escrow frozen pending admin review");
      setDisputeOpen(false); setDisputeReason(""); setDisputeDetails("");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const currentIdx = order.status === "disputed"
    ? STEPS.findIndex(s => s.key === order.dispute?.from_status)
    : STEPS.findIndex(s => s.key === order.status);

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mono text-xs text-stone-500">{order.order_id}</div>
          <h1 className="heading text-3xl font-extrabold tracking-tight">{order.lot_title}</h1>
          <div className="text-sm text-stone-600 mt-1">Buyer: {order.buyer_name} · Seller: {order.seller_name}</div>
        </div>
        <div className="text-right" data-testid="order-fee-breakdown">
          <div className="text-xs uppercase tracking-widest text-stone-400">Total escrow</div>
          <div className="heading text-3xl font-extrabold mono" data-testid="order-total">{fmtDual(order.total)}</div>
          <div className="text-xs mono text-stone-500 mt-1" data-testid="order-fee">Platform fee (6%): −{fmt(order.platform_fee ?? Math.round(order.total * 0.06 * 100) / 100)}</div>
          <div className="text-xs mono text-emerald-700" data-testid="order-payout">Seller payout: {fmt(order.seller_payout ?? Math.round((order.total - (order.platform_fee ?? order.total * 0.06)) * 100) / 100)}</div>
          {order.credit_applied > 0 && <div className="text-xs mono text-[#0284C7]" data-testid="order-credit">Account credit applied: −{fmt(order.credit_applied)}</div>}
        </div>
      </div>

      <div className="mt-8 surface-card p-6">
        <h3 className="heading font-semibold mb-4">Escrow stepper</h3>
        <ol className="space-y-3">
          {STEPS.map((s, i) => {
            const done = i <= currentIdx;
            const active = i === currentIdx;
            return (
              <li key={s.key} data-testid={`escrow-step-${s.key}`} className="flex items-center gap-3">
                {done ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : active ? <Clock className="h-5 w-5 text-amber-600" /> : <Circle className="h-5 w-5 text-stone-300" />}
                <div className={`text-sm ${done ? "text-stone-900" : "text-stone-400"}`}>{s.label}</div>
              </li>
            );
          })}
        </ol>
      </div>

      {order.status === "disputed" && (
        <div data-testid="order-dispute-banner" className="mt-6 surface-card p-5 border-l-4 border-red-500">
          <div className="flex items-center gap-2 text-red-700 font-semibold"><AlertTriangle className="h-4 w-4" /> Dispute open — escrow frozen</div>
          <div className="text-sm text-stone-600 mt-2">Reason: <span className="mono">{order.dispute?.reason}</span></div>
          {order.dispute?.details && <div className="text-sm text-stone-600 mt-1">{order.dispute.details}</div>}
          <div className="text-xs text-stone-500 mt-2">Raised {new Date(order.dispute?.raised_at).toLocaleString()} · Our team is reviewing this case.</div>
        </div>
      )}
      {order.status === "refunded" && (
        <div data-testid="order-refunded-banner" className="mt-6 surface-card p-5 border-l-4 border-emerald-500">
          <div className="flex items-center gap-2 text-emerald-700 font-semibold"><CheckCircle2 className="h-4 w-4" /> Refunded — payment hold voided</div>
          <div className="text-xs text-stone-500 mt-1">This order's escrow hold was voided following dispute resolution.</div>
        </div>
      )}

      <div className="mt-6 surface-card p-6 flex flex-wrap gap-3">
        <h3 className="heading font-semibold w-full mb-2">Actions</h3>
        <Button data-testid="order-invoice-btn" variant="outline" onClick={() => downloadInvoice(orderId)}>Download invoice (PDF)</Button>
        {user?.role === "seller" && order.status === "escrow_locked" && (
          <Button data-testid="order-verify-health-btn" onClick={() => act("verify-health")} disabled={busy} className="btn-forest">Mark health certs verified</Button>
        )}
        {(user?.role === "seller" || user?.role === "service_provider") && order.status === "health_verified" && (
          <Button data-testid="order-dispatch-btn" onClick={() => act("dispatch")} disabled={busy} className="btn-forest">Dispatch (LN₂)</Button>
        )}
        {user?.role === "buyer" && order.status === "dispatched" && (
          <Button data-testid="order-confirm-receipt-btn" onClick={() => act("confirm-receipt")} disabled={busy} className="btn-earth">Confirm receipt & release escrow</Button>
        )}
        {(user?.user_id === order.buyer_id || user?.user_id === order.seller_id) && ["escrow_locked", "health_verified", "dispatched"].includes(order.status) && (
          <Button data-testid="order-raise-dispute-btn" variant="outline" onClick={() => setDisputeOpen(true)} disabled={busy} className="border-red-300 text-red-700 hover:bg-red-50">
            <AlertTriangle className="h-4 w-4 mr-1" /> Raise dispute
          </Button>
        )}
        {order.status === "released" && (
          <div className="w-full">
            <div className="text-sm text-emerald-700 mb-3">✓ Funds released. Please rate the other party.</div>
            <div className="grid md:grid-cols-3 gap-3 items-end">
              <div>
                <label className="text-xs uppercase tracking-widest text-stone-500">Stars</label>
                <div className="mt-1 border rounded-md px-3 py-2"><StarRating testId="order-rating-stars" value={ratingStars} interactive onChange={setRatingStars} size="h-6 w-6" /></div>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-widest text-stone-500">Comment</label>
                <Textarea value={ratingComment} onChange={(e) => setRatingComment(e.target.value)} />
              </div>
            </div>
            <Button data-testid="order-submit-rating-btn" onClick={submitRating} className="btn-earth mt-3">Submit rating</Button>
          </div>
        )}
      </div>

      <div className="mt-6">
        <ColdChainLog orderId={orderId} status={order.status} />
      </div>

      <div className="mt-6 surface-card p-6">
        <h3 className="heading font-semibold mb-3">Timeline</h3>
        <ul className="text-sm space-y-2">
          {(order.escrow_history || []).flat().map((h, i) => (
            <li key={i} className="flex items-center justify-between border-b py-2">
              <span className="mono uppercase text-xs">{h.state}</span>
              <span className="text-stone-500 mono text-xs">{new Date(h.at).toLocaleString()}</span>
            </li>
          ))}
        </ul>
        {order.payment_ref && <div className="mt-3 text-xs mono text-stone-500" data-testid="payment-ref">Payment reference: {order.payment_ref}</div>}
      </div>

      <Dialog open={disputeOpen} onOpenChange={setDisputeOpen}>
        <DialogContent data-testid="dispute-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-600" /> Raise a dispute</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Raise a dispute to freeze this order's escrow for admin review.</DialogDescription>
          <div className="text-sm text-stone-600">This freezes the escrow and alerts our team. Funds stay locked — or are refunded — per the admin decision.</div>
          <div className="mt-3">
            <label className="text-xs uppercase tracking-widest text-stone-500">Reason</label>
            <Select value={disputeReason} onValueChange={setDisputeReason}>
              <SelectTrigger data-testid="dispute-reason-select" className="mt-1"><SelectValue placeholder="Select a reason" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Health certificate mismatch">Health certificate mismatch</SelectItem>
                <SelectItem value="Cold-chain excursion">Cold-chain excursion</SelectItem>
                <SelectItem value="Wrong or damaged material">Wrong or damaged material</SelectItem>
                <SelectItem value="Shipment never arrived">Shipment never arrived</SelectItem>
                <SelectItem value="Seller unresponsive">Seller unresponsive</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="mt-3">
            <label className="text-xs uppercase tracking-widest text-stone-500">Details</label>
            <Textarea data-testid="dispute-details-input" value={disputeDetails} onChange={(e) => setDisputeDetails(e.target.value)} placeholder="Describe the issue (optional)" />
          </div>
          <Button data-testid="dispute-submit-btn" onClick={raiseDispute} disabled={busy || !disputeReason} className="mt-4 bg-red-600 hover:bg-red-700 text-white">Submit dispute</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
